import { useEffect, useState, type ReactNode } from "react";
import { purchases } from "../../api/client";
import type { BillStatus, PaymentMade, PurchaseLine, PurchaseLineIn, PurchaseMeta, PurchaseOrderStatus, RecurrenceStatus, Vendor, VendorCreditStatus } from "../../api/types";
import { useLoad } from "../../api/useLoad";
import { Trash } from "../icons";
import type { CurrencyOf } from "../../api/currency";
import { fmtMoney } from "../currency/money";
import { Field, type Tone } from "../parts";
import "./purchases.css";
import { useInventoryMeta } from "../inventory/common";
import { LineTracking } from "../inventory/LineTracking";
import type { DetailRow } from "../../api/inv-depth";
import { t as tr } from "../../i18n";

/* What the Purchases screens share: status colours, the categories list, the vendor picker and the line-item editor. */

export const BILL_TONE: Record<BillStatus, Tone> = { open: "gold", overdue: "red", paid: "green", void: "neutral" };
export const PO_TONE: Record<PurchaseOrderStatus, Tone> = { open: "gold", received: "amber", billed: "green", cancelled: "neutral" };
export const CREDIT_TONE: Record<VendorCreditStatus, Tone> = { open: "gold", closed: "green", void: "neutral" };
export const PROFILE_TONE: Record<RecurrenceStatus, Tone> = { active: "green", stopped: "neutral", expired: "neutral" };
export const PAYMENT_TONE: Record<PaymentMade["status"], Tone> = { paid: "green", void: "neutral" };

export const errorText = (e: unknown, fallback = tr("That could not be saved.")) => (e instanceof Error ? e.message : fallback);

/** ₹ with paise; in a bill's or payment's own currency when it is in another one (src/ui/currency). */
export const inr = (v: string | null | undefined, cur?: CurrencyOf | null) => fmtMoney(v, cur ?? null, true);

export const FREQUENCY_LABEL = { day: "day", week: "week", month: "month", year: "year" } as const;
export const everyText = (n: number, f: keyof typeof FREQUENCY_LABEL) => (n === 1 ? tr("Every {f}", { f }) : tr("Every {n} {f}s", { n, f }));

export const termsLabel = (days: number) => (days === 0 ? tr("Due on receipt") : tr("Net {days}", { days }));

let metaCache: PurchaseMeta | null = null;
/** Categories, paid-through accounts, payment modes and GST rates. Shown from the last answer while a fresh one loads. */
export function usePurchaseMeta(): PurchaseMeta | null {
  const [meta, setMeta] = useState<PurchaseMeta | null>(metaCache);
  useEffect(() => {
    let live = true;
    purchases
      .meta()
      .then((m) => {
        metaCache = m;
        if (live) setMeta(m);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return meta;
}

export const modeLabel = (meta: PurchaseMeta | null, mode: string) => meta?.payment_modes.find((m) => m.value === mode)?.label ?? mode;

/** Picks one of the active vendors (and keeps a vendor that has since gone inactive visible, so an old document still shows its vendor). */
export function VendorSelect({ value, onChange, disabled, label = "Vendor" }: { value: string; onChange: (id: string, vendor: Vendor | null) => void; disabled?: boolean; label?: string }) {
  const vendors = useLoad(() => purchases.vendors({}), []);
  const list = (vendors.data ?? []).filter((v) => v.status === "active" || v.id === value);
  return (
    <Field label={label}>
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value, list.find((v) => v.id === e.target.value) ?? null)}>
        <option value="">{vendors.data ? (list.length ? tr("Choose a vendor…") : tr("No vendors yet: add one in Vendors")) : tr("Loading…")}</option>
        {list.map((v) => (
          <option key={v.id} value={v.id}>
            {v.name}
            {v.status !== "active" ? tr(" (inactive)") : ""}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function TagsField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="Tags" hint="Separate with commas" full>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={tr("cloud, quarterly")} />
    </Field>
  );
}
export const tagList = (s: string) => s.split(",").map((t) => t.trim()).filter(Boolean);

/* ---------- line items ---------- */

export type LineDraft = { description: string; account_id: string; quantity: string; rate: string; gst_rate: string; hsn_sac: string; itc_eligible: boolean; item_id?: string; tracking?: DetailRow[] };

export const emptyLine = (): LineDraft => ({ description: "", account_id: "exp_other", quantity: "1", rate: "", gst_rate: "18", hsn_sac: "", itc_eligible: true });

export const draftsFrom = (lines: Pick<PurchaseLine, "description" | "account_id" | "quantity" | "rate" | "gst_rate" | "hsn_sac" | "itc_eligible" | "item_id">[]): LineDraft[] =>
  lines.map((l) => ({ description: l.description, account_id: l.account_id, quantity: String(l.quantity), rate: l.rate, gst_rate: String(l.gst_rate), hsn_sac: l.hsn_sac ?? "", itc_eligible: l.itc_eligible, item_id: l.item_id ?? "" }));

export const toLines = (drafts: LineDraft[]): PurchaseLineIn[] =>
  drafts.map((l) => ({
    ...(l.item_id !== undefined ? { item_id: l.item_id || null } : {}),
    description: l.description,
    account_id: l.account_id,
    quantity: l.quantity === "" ? 1 : l.quantity,
    rate: l.rate,
    gst_rate: Number(l.gst_rate) || 0,
    hsn_sac: l.hsn_sac.trim() || null,
    itc_eligible: l.itc_eligible,
    ...(l.tracking ? { tracking: l.tracking } : {}),
  }));

/** The same split the server makes, for the running total on a form. The server's figures are the ones kept. */
export function estimate(drafts: LineDraft[], inclusive: boolean) {
  let subtotal = 0;
  let tax = 0;
  for (const l of drafts) {
    const rate = Math.round(Number(l.rate) * 100);
    const qty = Number(l.quantity === "" ? 1 : l.quantity);
    if (!Number.isFinite(rate) || !Number.isFinite(qty) || rate < 0 || qty <= 0) continue;
    const amount = Math.round(qty * rate);
    const pct = Number(l.gst_rate) || 0;
    const taxable = inclusive ? Math.round((amount * 100) / (100 + pct)) : amount;
    const t = inclusive ? amount - taxable : Math.round((amount * pct) / 100);
    subtotal += taxable;
    tax += t;
  }
  return { subtotal: (subtotal / 100).toFixed(2), tax: (tax / 100).toFixed(2), total: ((subtotal + tax) / 100).toFixed(2) };
}

export function Totals({ drafts, inclusive, cur }: { drafts: LineDraft[]; inclusive: boolean; cur?: CurrencyOf | null }) {
  const t = estimate(drafts, inclusive);
  return (
    <div className="pur-totals" aria-live="polite">
      <div>
        <span>{tr("Sub total")}</span>
        <span className="num">{inr(t.subtotal, cur)}</span>
      </div>
      <div>
        <span>GST</span>
        <span className="num">{inr(t.tax, cur)}</span>
      </div>
      <div>
        <span>{tr("Total")}</span>
        <span className="num">{inr(t.total, cur)}</span>
      </div>
      <small>{tr("An estimate. CGST + SGST or IGST is set by the vendor's state when you save.")}</small>
    </div>
  );
}

/** track: serials and batches on lines naming a tracked item: a bill's come in, a goods return's go out (src/ui/inventory/LineTracking.tsx). */
export function LinesEditor({ lines, onChange, meta, cur, track = null }: { lines: LineDraft[]; onChange: (l: LineDraft[]) => void; meta: PurchaseMeta | null; cur?: CurrencyOf | null; track?: { direction: "in" | "out"; locationId?: string | null; date?: string | null } | null }) {
  const expense = (meta?.accounts ?? []).filter((a) => a.group === "expense");
  // Goods tracked in inventory (server/inventory.js): a line that names one brings it into stock. Nothing shows while inventory is off.
  const tracked = useInventoryMeta()?.tracked_items ?? [];
  const set = (i: number, patch: Partial<LineDraft>) => onChange(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  return (
    <div className="pur-lines" role="group" aria-label={tr("Line items")}>
      {lines.map((l, i) => {
        return (
          <div className="pur-line" key={i}>
            <Field label={tr("Item {n}", { n: i + 1 })}>
              <input value={l.description} onChange={(ev) => set(i, { description: ev.target.value })} placeholder={tr("What was bought")} />
            </Field>
            <Field label="Category">
              <select value={l.account_id} onChange={(ev) => set(i, { account_id: ev.target.value })}>
                {expense.length === 0 && <option value={l.account_id}>{l.account_id}</option>}
                {expense.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Qty">
              <input inputMode="decimal" value={l.quantity} onChange={(ev) => set(i, { quantity: ev.target.value })} />
            </Field>
            <Field label={tr("Rate ({v})", { v: cur?.currency_code && cur.currency_code !== "INR" ? cur.currency_code : "₹" })}>
              <input inputMode="decimal" value={l.rate} onChange={(ev) => set(i, { rate: ev.target.value })} placeholder="0.00" />
            </Field>
            <Field label="GST %">
              <select value={l.gst_rate} onChange={(ev) => set(i, { gst_rate: ev.target.value })}>
                {(meta?.gst_rates ?? [0, 5, 12, 18, 28]).map((r) => (
                  <option key={r} value={String(r)}>
                    {r}%
                  </option>
                ))}
              </select>
            </Field>
            <Field label="HSN / SAC">
              <input inputMode="numeric" value={l.hsn_sac} onChange={(ev) => set(i, { hsn_sac: ev.target.value.replace(/\D/g, "").slice(0, 8) })} />
            </Field>
            <div className="pur-line__foot">
              <label className="pur-check">
                <input type="checkbox" checked={l.itc_eligible} onChange={(ev) => set(i, { itc_eligible: ev.target.checked })} />
                ITC
              </label>
              <button type="button" className="icon-button" aria-label={tr("Remove item {n}", { n: i + 1 })} disabled={lines.length === 1} onClick={() => onChange(lines.filter((_, k) => k !== i))}>
                <Trash size={16} />
              </button>
            </div>
            {(tracked.length > 0 || l.item_id) && (
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Inventory item" hint="Brings the quantity into stock at this line's cost.">
                  <select
                    value={l.item_id ?? ""}
                    onChange={(ev) => {
                      const it = tracked.find((x) => x.id === ev.target.value);
                      set(i, { item_id: ev.target.value, ...(it && !l.description.trim() ? { description: it.name } : {}), ...(it?.hsn_sac && !l.hsn_sac ? { hsn_sac: it.hsn_sac } : {}), ...(it?.purchase_rate && l.rate === "" ? { rate: it.purchase_rate } : {}) });
                    }}
                  >
                    <option value="">{tr("Not a stock item")}</option>
                    {tracked.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                        {x.sku ? ` · ${x.sku}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
            {track && l.item_id && (
              <div style={{ gridColumn: "1 / -1" }}>
                <LineTracking line={i + 1} itemId={l.item_id} quantity={l.quantity} direction={track.direction} locationId={track.locationId} date={track.date} value={l.tracking} onChange={(rows) => set(i, { tracking: rows })} />
              </div>
            )}
          </div>
        );
      })}
      <button type="button" className="button button--secondary button--sm" style={{ justifySelf: "start" }} onClick={() => onChange([...lines, emptyLine()])}>
        {tr("Add a line")}
      </button>
    </div>
  );
}

/** A read-only table of a saved document's lines. */
export function LinesTable({ lines, cur }: { lines: PurchaseLine[]; cur?: CurrencyOf | null }) {
  return (
    <table className="pur-mini">
      <thead>
        <tr>
          <th>{tr("Item")}</th>
          <th className="is-r">{tr("Qty")}</th>
          <th className="is-r">{tr("Rate")}</th>
          <th className="is-r">GST</th>
          <th className="is-r">{tr("Amount")}</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id}>
            <td>
              {l.description}
              <br />
              <small className="crm-note" style={{ padding: 0, background: "none" }}>
                {l.account_name}
                {l.hsn_sac ? ` · HSN/SAC ${l.hsn_sac}` : ""}
                {l.itc_eligible ? "" : tr(" · no ITC")}
              </small>
            </td>
            <td className="is-r num">{l.quantity}</td>
            <td className="is-r num">{inr(l.rate, cur)}</td>
            <td className="is-r num">{l.gst_rate}%</td>
            <td className="is-r num">{inr(l.total, cur)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Sub total, the GST lines that apply, and the total of a saved document. */
export function PricedFacts({ d }: { d: { subtotal: string; cgst_amount: string; sgst_amount: string; igst_amount: string; total: string; supply_type: string; tax_inclusive: boolean } & CurrencyOf }): ReactNode {
  return (
    <div className="pur-totals">
      <div>
        <span>{tr("Sub total")}</span>
        <span className="num">{inr(d.subtotal, d)}</span>
      </div>
      {d.supply_type === "inter_state" ? (
        <div>
          <span>IGST</span>
          <span className="num">{inr(d.igst_amount, d)}</span>
        </div>
      ) : (
        <>
          <div>
            <span>CGST</span>
            <span className="num">{inr(d.cgst_amount, d)}</span>
          </div>
          <div>
            <span>SGST</span>
            <span className="num">{inr(d.sgst_amount, d)}</span>
          </div>
        </>
      )}
      <div>
        <span>{tr("Total")}</span>
        <span className="num">{inr(d.total, d)}</span>
      </div>
      <small>{d.tax_inclusive ? tr("Rates included GST.") : tr("Rates were before GST.")}</small>
    </div>
  );
}
